import React from "react";
import PropTypes from "prop-types";
import Page from "../../../components/BaseComponents/Page/Page";
import {PageFooter} from "../../../components/BaseComponents/Page/PageFooter";
import {FaSolidIcon} from "../../../components/FontAwesomeIcons";
import {swal, swalError, swalLoading, swalSuccess} from "../../../helpers/SweetAlertWrapper";
import {UsersModuleEventsAPI} from "../../../api/UsersModuleEvents/UsersModuleEventsAPI";

export class UserEventBookPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            name: "",
            surname: "",
            participantsNumber: 1,
            participants: [this.getEmptyParticipant()],
            notes: "",
            isSaving: false,
        }
        this.iconBoxStyle = {
            width: "38px",
            minWidth: "38px",
            height: "38px",
        };
    }

    getEmptyParticipant = _ => ({
        name: "",
        has_intolerances: false,
        intolerances: "",
    })

    getSettings = _ => {
        const eventInfo = this.props.eventInfo || {};
        return eventInfo.settings || {};
    }

    isSettingEnabled = setting => `${this.getSettings()[setting]}` === "1"

    canShowParticipantsNames = _ => this.isSettingEnabled("collect_all_participants_names")

    canShowIntolerances = _ => this.isSettingEnabled("show_intolerances")

    canShowNotes = _ => this.isSettingEnabled("show_notes")

    getMaxParticipantsPerReservation = _ => {
        const eventInfo = this.props.eventInfo || {};
        const maxParticipants = parseInt(eventInfo.max_participants_per_reservation, 10);
        return !Number.isNaN(maxParticipants) && maxParticipants > 0 ? maxParticipants : null;
    }

    getNormalizedParticipantsNumber = value => {
        const maxParticipants = this.getMaxParticipantsPerReservation();
        const parsedValue = parseInt(value, 10);
        let participantsNumber = !Number.isNaN(parsedValue) && parsedValue > 0 ? parsedValue : 1;
        if (maxParticipants && participantsNumber > maxParticipants) {
            participantsNumber = maxParticipants;
        }
        return participantsNumber;
    }

    getParticipantsForNumber = participantsNumber => {
        const participants = [...this.state.participants];
        while (participants.length < participantsNumber) {
            participants.push(this.getEmptyParticipant());
        }
        return participants.slice(0, participantsNumber);
    }

    handleInputChange = field => e => {
        this.setState({
            [field]: e.target.value,
        });
    }

    handleParticipantsNumberChange = e => {
        const value = e.target.value;
        if (value === "") {
            this.setState({
                participantsNumber: "",
                participants: [],
            });
            return;
        }

        const participantsNumber = this.getNormalizedParticipantsNumber(value);
        this.setState({
            participantsNumber: participantsNumber,
            participants: this.getParticipantsForNumber(participantsNumber),
        });
    }

    handleParticipantsNumberBlur = _ => {
        const participantsNumber = this.getNormalizedParticipantsNumber(this.state.participantsNumber);
        this.setState({
            participantsNumber: participantsNumber,
            participants: this.getParticipantsForNumber(participantsNumber),
        });
    }

    handleParticipantNameChange = index => e => {
        const participants = [...this.state.participants];
        participants[index] = {
            ...participants[index],
            name: e.target.value,
        };
        this.setState({participants: participants});
    }

    handleParticipantIntolerancesToggle = index => e => {
        const participants = [...this.state.participants];
        participants[index] = {
            ...participants[index],
            has_intolerances: e.target.checked,
            intolerances: e.target.checked ? participants[index].intolerances : "",
        };
        this.setState({participants: participants});
    }

    handleParticipantIntolerancesChange = index => e => {
        const participants = [...this.state.participants];
        participants[index] = {
            ...participants[index],
            intolerances: e.target.value,
        };
        this.setState({participants: participants});
    }

    getValidationErrors = _ => {
        const errors = [];
        const participantsNumber = this.getNormalizedParticipantsNumber(this.state.participantsNumber);
        const maxParticipants = this.getMaxParticipantsPerReservation();

        if (!this.state.name.trim()) {
            errors.push("Nome");
        }
        if (!this.state.surname.trim()) {
            errors.push("Cognome");
        }
        if (!participantsNumber || participantsNumber < 1) {
            errors.push("Numero partecipanti");
        }
        if (maxParticipants && participantsNumber > maxParticipants) {
            errors.push(`Numero partecipanti massimo ${maxParticipants}`);
        }
        if (this.canShowParticipantsNames()) {
            this.getParticipantsForNumber(participantsNumber).forEach((participant, index) => {
                if (!participant.name.trim()) {
                    errors.push(`Nome partecipante ${index + 1}`);
                }
            });
        }
        return errors;
    }

    isFormValid = _ => !this.getValidationErrors().length

    showValidationError = _ => {
        const errors = this.getValidationErrors();
        swalError(
            <div className={"text-left"}>
                <div className={"font-weight-bold mb-2"}>Per salvare compila:</div>
                <ul className={"mb-0 pl-3"}>
                    {errors.map(error => <li key={error}>{error}</li>)}
                </ul>
            </div>
        );
    }

    getReservationPayload = _ => {
        const eventInfo = this.props.eventInfo || {};
        const participantsNumber = this.getNormalizedParticipantsNumber(this.state.participantsNumber);
        const participants = this.getParticipantsForNumber(participantsNumber).map(participant => ({
            name: participant.name.trim(),
            has_intolerances: this.canShowIntolerances() ? !!participant.has_intolerances : false,
            intolerances: this.canShowIntolerances() && participant.has_intolerances ? participant.intolerances.trim() : "",
        }));
        const payload = {
            event_id: eventInfo.event_id,
            name: this.state.name.trim(),
            surname: this.state.surname.trim(),
            participants_number: participantsNumber,
            participants: participants,
        };

        if (this.canShowNotes()) {
            payload.notes = this.state.notes.trim();
        }
        return payload;
    }

    handleSave = _ => {
        if (!this.isFormValid()) {
            this.showValidationError();
            return;
        }

        this.setState({isSaving: true});
        swalLoading("Creazione prenotazione in corso...");
        UsersModuleEventsAPI.Admin.Reservations.book(this.getReservationPayload())
            .then(response => {
                swal.safeClose();
                this.setState({isSaving: false});
                if (this.props.onBooked) {
                    this.props.onBooked(response);
                } else if (this.props.onDismiss) {
                    this.props.onDismiss();
                }
                swalSuccess(response.message || "Prenotazione creata correttamente.");
            })
            .catch(error => {
                swal.safeClose();
                this.setState({isSaving: false});
                swalError(error);
            });
    }

    renderParticipant = (participant, index) => {
        const checkboxId = `userEventBookParticipantIntolerances${index}`;
        return (
            <div className={"card border-0 shadow-sm mb-3"} key={index}>
                <div className={"card-body"}>
                    <div className={"d-flex align-items-center mb-3"}>
                        <div
                            className={"rounded bg-light d-flex align-items-center justify-content-center text-primary mr-3"}
                            style={this.iconBoxStyle}
                        >
                            <FaSolidIcon name={"user"}/>
                        </div>
                        <div className={"font-weight-bold"}>Partecipante {index + 1}</div>
                    </div>

                    {
                        this.canShowParticipantsNames() &&
                        <div className={"form-group"}>
                            <label className={"small font-weight-bold"}>Nome partecipante</label>
                            <input
                                type={"text"}
                                className={"form-control"}
                                value={participant.name}
                                onChange={this.handleParticipantNameChange(index)}
                            />
                        </div>
                    }

                    {
                        this.canShowIntolerances() &&
                        <React.Fragment>
                            <div className={"custom-control custom-checkbox"}>
                                <input
                                    type={"checkbox"}
                                    className={"custom-control-input"}
                                    id={checkboxId}
                                    checked={participant.has_intolerances}
                                    onChange={this.handleParticipantIntolerancesToggle(index)}
                                />
                                <label className={"custom-control-label"} htmlFor={checkboxId}>
                                    Allergie o intolleranze
                                </label>
                            </div>
                            {
                                participant.has_intolerances &&
                                <div className={"form-group mt-3 mb-0"}>
                                    <label className={"small font-weight-bold"}>Dettagli allergie/intolleranze</label>
                                    <textarea
                                        className={"form-control"}
                                        rows={"3"}
                                        value={participant.intolerances}
                                        onChange={this.handleParticipantIntolerancesChange(index)}
                                    />
                                </div>
                            }
                        </React.Fragment>
                    }
                </div>
            </div>
        );
    }

    render() {
        const maxParticipants = this.getMaxParticipantsPerReservation();
        return (
            <Page
                title={"Aggiungi adesione"}
                leftIcon={<FaSolidIcon name={"chevron-left"}/>}
                onLeftIconClick={this.props.onDismiss}
                active={true}
                footer={
                    <PageFooter className={"bg-white border-top shadow p-3"}>
                        <button
                            type={"button"}
                            className={"btn btn-primary btn-lg btn-block"}
                            onClick={this.handleSave}
                            disabled={this.state.isSaving}
                        >
                            Salva <FaSolidIcon name={"save"} className={"ml-1"}/>
                        </button>
                    </PageFooter>
                }
            >
                <div className={"container py-3"}>
                    <div className={"card border-0 shadow-sm mb-3"}>
                        <div className={"card-body"}>
                            <h6 className={"font-weight-bold mb-3"}>Dati prenotazione</h6>

                            <div className={"form-row"}>
                                <div className={"form-group col-12 col-md-6"}>
                                    <label className={"small font-weight-bold"}>Nome</label>
                                    <input
                                        type={"text"}
                                        className={"form-control"}
                                        value={this.state.name}
                                        onChange={this.handleInputChange("name")}
                                    />
                                </div>
                                <div className={"form-group col-12 col-md-6"}>
                                    <label className={"small font-weight-bold"}>Cognome</label>
                                    <input
                                        type={"text"}
                                        className={"form-control"}
                                        value={this.state.surname}
                                        onChange={this.handleInputChange("surname")}
                                    />
                                </div>
                            </div>

                            <div className={"form-group mb-0"}>
                                <label className={"small font-weight-bold"}>Numero partecipanti</label>
                                <input
                                    type={"number"}
                                    min={"1"}
                                    max={maxParticipants || undefined}
                                    step={"1"}
                                    className={"form-control"}
                                    value={this.state.participantsNumber}
                                    onChange={this.handleParticipantsNumberChange}
                                    onBlur={this.handleParticipantsNumberBlur}
                                />
                                {
                                    maxParticipants &&
                                    <small className={"form-text text-muted"}>
                                        Massimo {maxParticipants} partecipanti per prenotazione.
                                    </small>
                                }
                            </div>
                        </div>
                    </div>

                    {
                        (this.canShowParticipantsNames() || this.canShowIntolerances()) &&
                        <React.Fragment>
                            <h6 className={"font-weight-bold mb-2"}>Partecipanti</h6>
                            {this.state.participants.map(this.renderParticipant)}
                        </React.Fragment>
                    }

                    {
                        this.canShowNotes() &&
                        <div className={"card border-0 shadow-sm mb-3"}>
                            <div className={"card-body"}>
                                <div className={"form-group mb-0"}>
                                    <label className={"small font-weight-bold"}>Note</label>
                                    <textarea
                                        className={"form-control"}
                                        rows={"4"}
                                        value={this.state.notes}
                                        onChange={this.handleInputChange("notes")}
                                    />
                                </div>
                            </div>
                        </div>
                    }

                    <div className={"mb-4"}/>
                </div>
            </Page>
        );
    }
}

UserEventBookPage.propTypes = {
    onDismiss: PropTypes.func,
    onBooked: PropTypes.func,
    eventInfo: PropTypes.object
}
